<template>
  <div
    v-if="value"
    v-loading="loading"
    class="tournament-light-grey rounded pa-4"
    style="min-height: 212px"
    element-loading-text="Carregando..."
  >
    <div v-if="isEditable" class="row">
      <div class="column flex">
        <v-combobox
          v-model="selectedUser"
          color="tournament-grey"
          class="mt-0 tournament-grey--text"
          placeholder="Buscar integrantes existentes"
          no-data-text="Nada encontrado"
          :loading="loadingUsers"
          clearable
          :items="remoteUsers"
          :item-text="labelForUser"
          :item-disabled="isUserDisabled"
          :error="!!error"
          :error-messages="error"
          return-object
          @input.native="fetch()"
          @focus="fetch()"
        >
          <template #item="{ index, item }">
            <v-list-tile-content>
              {{ item.name }}
            </v-list-tile-content>
            <v-spacer />
            <v-list-tile-action>
              <v-chip color="tournament-grey white--text" dark label small>
                {{ item.alias }}
              </v-chip>
            </v-list-tile-action>
          </template>
        </v-combobox>
      </div>
      <el-button
        v-if="!selectedUser"
        key="create"
        style="height: 36px; width: 180px"
        class="ml-4 tournament-light-blue tournament-white--text rounded no-border"
        @click="createUser()"
      >
        CRIAR
      </el-button>

      <el-button
        v-else
        key="add"
        style="height: 36px; width: 180px"
        icon="el-icon-plus"
        class="ml-4 tournament-green tournament-white--text rounded no-border"
        @click="addMemberInEnrollment(selectedUser)"
      >
        Adicionar
      </el-button>
    </div>

    <!-- Team members name -->
    <div v-if="enrollmentStatus && value.length" class="mt-4">
      <UsersCard
        v-for="user in value"
        :key="user._id"
        :user="user"
        class="mb-3"
        :documents="documents"
        :enrollment-status="enrollmentStatus"
        @remove="confirmRemoveMember($event)"
        @edit="edit($event)"
      />
    </div>
  </div>
</template>

<script>
import _ from "lodash";

import Team from "@/services/Team";
import Enrollment from "@/services/Enrollment";

import UserEditor from "@/components/Users/Editor";
import UsersCard from "@/components/Users/Card";

import ParseHTTPError from "@/helpers/ParseHTTPError";

export default {
  name: "EnrollmentSubmitMembers",

  components: {
    UsersCard,
  },

  props: {
    value: {
      type: Array,
    },
    enrollmentStatus: {
      type: String,
      required: true,
    },
    enrollmentId: {
      type: String,
      required: true,
    },
    teamId: {
      type: String,
    },
    documents: {},
  },
  data() {
    return {
      remoteUsers: [],
      selectedUser: null,
      error: null,
      loading: true,

      loadingUsers: false,
      memberSearch: "",
    };
  },

  computed: {
    isEditable() {
      return this.enrollmentStatus == "waiting";
    },
    users() {
      return [].concat(this.remoteUsers || []).concat(this.value || []);
    },
  },

  created() {
    // Keep safe when parent pass a undefined as prop
    if (!this.value) {
      return this.$emit("input", []);
    }
    this.fetch();
  },

  methods: {
    createUser() {
      this.$dialog(
        {
          width: this.$vuetify.breakpoint.xsOnly ? "95%" : "608px",
          type: "member",
          teamId: this.teamId,
          enrollmentStatus: this.enrollmentStatus,
          closeOnClickModal: false,
        },
        UserEditor,
      )
        .then((user) => {
          this.addMemberInEnrollment(user);
        })
        .catch(() => {});
    },

    fetch() {
      this.loadingUsers = true;
      Team.members({ roles: "team:member", search: this.memberSearch }).then(
        (users) => {
          this.loading = false;
          this.loadingUsers = false;
          this.remoteUsers = users.data;
        },
        () => {
          this.loadingUsers = false;
          this.loading = false;
        },
      );
    },

    addMemberInEnrollment(user) {
      if (!_.get(user, "_id")) return;

      this.error = null;
      this.loading = true;
      Enrollment.addMember(this.enrollmentId, user._id).then(
        (res) => {
          this.loading = false;
          this.selectedUser = null;
          this.$emit("input", res.data);
        },
        (err) => {
          this.loading = false;
          const status = ParseHTTPError(err);
          this.error = status.error.split(":")[1] || status.error;

          if (!status.type) {
            this.error =
              "Houve um erro na comunicação com o servidor, tente novamente";
          }

          this.$notify.error({
            title: "Erro",
            message: this.error,
          });
        },
      );
    },

    removeMember(userId) {
      this.loading = true;
      Enrollment.removeMember(this.enrollmentId, userId).then(
        (res) => {
          this.loading = false;
          this.$emit("input", res.data);
        },
        (err) => {
          this.loading = false;
          const status = ParseHTTPError(err);
          let error = status.error;

          if (!status.type) {
            error =
              "Houve um erro na comunicação com o servidor, tente novamente";
          }

          this.$notify.error({
            title: "Erro",
            message: error,
          });
        },
      );
    },

    confirmRemoveMember(userId) {
      this.$dialog({
        width: "350px",
        customClass: "tournament-white pa-4",
        title:
          "Você tem certeza que deseja remover esse integrante da inscrição?",
        buttons: [
          { name: "Cancelar", class: "tournament-grey--text ma-0" },
          {
            name: "Remover",
            action: "submit",
            class: "tournament-red--text ma-0",
          },
        ],
      })
        .then(() => {
          this.removeMember(userId);
        })
        .catch(() => {});
    },

    edit(user) {
      this.$dialog(
        {
          width: this.$vuetify.breakpoint.xsOnly ? "95%" : "608px",
          type: "member",
          modelId: user._id,
          cache: user,
          teamId: this.teamId,
          enrollmentStatus: this.enrollmentStatus,
          closeOnClickModal: false,
        },
        UserEditor,
      )
        .then((user) => {
          this.userUpdated(user);
        })
        .catch(() => {});
    },

    userUpdated: function (model) {
      // Check if has models listing
      if (!this.value || !model) {
        return;
      }

      const localValue = this.value;
      // Find index and replace model
      const index = localValue.findIndex((test) => test._id == model._id);
      if (index >= 0) {
        this.$set(localValue, index, model);
        this.$emit("input", localValue);
      } else {
        this.$emit("input", localValue.unshift(model));
      }
    },

    // Formats user label for combobox
    labelForUser(user) {
      return user.name + " (" + user.alias + ")";
    },

    // Set selected users as disabled
    isUserDisabled(check) {
      return this.value.some((user) => user._id == check._id);
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/Enrollments/Submit/Members.vue